import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api';

type Choice = { index: number; chosen: number; isCorrect: boolean; text: string };
type Row = {
  id: number; position: number; type: string; text: string; weight: number; tags: string[];
  answered: number; correct: number;
  facility: number | null; discrimination: number | null; awaitingReview: number;
  flag: 'likely_miskeyed' | 'too_easy' | 'too_hard' | 'not_discriminating' | null;
  choices: Choice[];
};
type Analysis = {
  unit: { id: number; name: string; type: string };
  course: { id: number; name: string };
  attempts: number;
  flagText: Record<string, string>;
  questions: Row[];
};

const FLAG_LABEL: Record<string, string> = {
  likely_miskeyed: 'Check the answer key',
  too_easy: 'Too easy',
  too_hard: 'Too hard',
  not_discriminating: 'Tells you nothing',
};

/** The list of tests, so somebody can pick one. */
export function QuestionStatsList() {
  const [tests, setTests] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setTests((await api.get<{ tests: any[] }>('/admin/question-stats')).tests); }
      catch (e) { setError((e as ApiError).message); }
    })();
  }, []);

  if (error) return <p className="auth__error">{error}</p>;
  if (!tests) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1 className="h1">How the questions are performing</h1>
      <p className="muted small">
        Which questions people get wrong, which wrong answer they pick, and which ones look
        as though the answer key is wrong.
      </p>

      {tests.length === 0 && <p className="notice">There are no tests to look at yet.</p>}

      <table className="grid">
        <thead><tr>
          <th>Test</th><th>Course</th><th className="num">Questions</th>
          <th className="num">Attempts</th><th className="num">Average</th>
        </tr></thead>
        <tbody>
          {tests.map((t) => (
            <tr key={t.unitId}>
              <td><Link to={`/admin/question-stats/${t.unitId}`}>{t.unitName}</Link></td>
              <td className="muted">{t.courseName}</td>
              <td className="num">{t.questionCount}</td>
              <td className="num">{t.attempts}</td>
              <td className="num">{t.averageScore === null ? '—' : `${t.averageScore}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** One test, question by question. */
export function QuestionStatsDetail() {
  const { unitId } = useParams();
  const [data, setData] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [onlyFlagged, setOnlyFlagged] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get(`/admin/question-stats/${unitId}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [unitId]);
  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const flagged = data.questions.filter((q) => q.flag);
  const miskeyed = flagged.filter((q) => q.flag === 'likely_miskeyed');
  const shown = onlyFlagged ? flagged : data.questions;

  return (
    <>
      <nav className="crumbs">
        <Link to="/admin/question-stats">Question performance</Link> <span aria-hidden>›</span> {data.unit.name}
      </nav>

      <div className="page-head">
        <h1 className="h1">{data.unit.name}</h1>
        <a className="btn btn--ghost" href={`/api/admin/question-stats/${unitId}/csv`}>Download</a>
      </div>
      <p className="muted small">{data.course.name} · {data.attempts} finished attempts</p>

      {data.attempts < 10 && (
        <p className="notice">
          Only {data.attempts} {data.attempts === 1 ? 'person has' : 'people have'} finished this
          test. Learnica will not judge a question on that few — the numbers below are shown, but
          nothing is flagged until at least ten attempts.
        </p>
      )}

      {miskeyed.length > 0 && (
        <p className="notice notice--warn">
          <strong>{miskeyed.length} {miskeyed.length === 1 ? 'question looks' : 'questions look'} miskeyed.</strong>{' '}
          {data.flagText.likely_miskeyed}
        </p>
      )}

      {flagged.length > 0 && (
        <p className="toolbar">
          <label className="check">
            <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} />
            Show only the {flagged.length} worth looking at
          </label>
        </p>
      )}

      <table className="grid grid--qstats">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Question</th>
            <th className="num">Answered</th>
            <th>Got it right</th>
            <th>Separates who knows it</th>
            <th>Worth a look</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((q) => (
            <QuestionRow key={q.id} q={q} flagText={data.flagText}
              open={open === q.id} onToggle={() => setOpen(open === q.id ? null : q.id)} />
          ))}
          {shown.length === 0 && (
            <tr><td colSpan={6} className="muted">Nothing flagged. The questions are behaving.</td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function QuestionRow({ q, flagText, open, onToggle }: {
  q: Row; flagText: Record<string, string>; open: boolean; onToggle: () => void;
}) {
  const pct = q.facility === null ? null : Math.round(q.facility * 100);

  return (
    <>
      <tr className={q.flag === 'likely_miskeyed' ? 'is-suspect' : ''}>
        <td className="num muted">{q.position}</td>
        <td>
          <button className="link-plain" onClick={onToggle} aria-expanded={open}>
            {q.text.slice(0, 110)}{q.text.length > 110 ? '…' : ''}
          </button>
          {q.awaitingReview > 0 && (
            <div className="muted small">{q.awaitingReview} waiting to be marked</div>
          )}
        </td>
        <td className="num">{q.answered}</td>

        <td>
          {pct === null ? <span className="muted">—</span> : (
            <span className="meter" title={`${q.correct} of ${q.answered}`}>
              <span className="meter__track"><span className="meter__fill" style={{ width: `${pct}%` }} /></span>
              <span className="meter__value">{pct}%</span>
            </span>
          )}
        </td>

        <td>
          {q.discrimination === null
            ? <span className="muted small">not enough attempts</span>
            : <Diverging value={q.discrimination} />}
        </td>

        <td>
          {q.flag
            ? <span className={`pill ${q.flag === 'likely_miskeyed' ? 'pill--failed' : 'pill--quiet'}`}
                title={flagText[q.flag]}>{FLAG_LABEL[q.flag]}</span>
            : <span className="muted">—</span>}
        </td>
      </tr>

      {open && (
        <tr className="detailrow">
          <td colSpan={6}>
            <p className="qfull">{q.text}</p>
            {q.flag && <p className="muted small">{flagText[q.flag]}</p>}

            {q.choices.length > 0 ? (
              <ul className="plain choices">
                {q.choices.map((c) => {
                  const total = q.choices.reduce((sum, x) => sum + x.chosen, 0) || 1;
                  const share = Math.round((c.chosen / total) * 100);
                  return (
                    <li key={c.index} className={c.isCorrect ? 'is-key' : ''}>
                      <span className="choices__label">
                        {c.isCorrect ? '✓ ' : ''}{c.text}
                      </span>
                      <span className="meter">
                        <span className="meter__track">
                          <span className={`meter__fill ${c.isCorrect ? 'meter__fill--key' : ''}`}
                            style={{ width: `${share}%` }} />
                        </span>
                        <span className="meter__value">{c.chosen}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted small">This question has no fixed options to count.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Discrimination, on a scale running from −1 to 1 with zero in the middle.
 *
 * A diverging bar rather than a number alone, because the sign is the whole
 * message: left of centre means the trainees who know the material are getting
 * this one wrong. The number is printed beside it, so the colour is never
 * carrying the meaning on its own.
 */
function Diverging({ value }: { value: number }) {
  const width = Math.min(Math.abs(value), 1) * 50;
  const negative = value < 0;
  return (
    <span className="diverge" title={`${value}`}>
      <span className="diverge__track">
        <span className="diverge__zero" />
        <span className={`diverge__fill ${negative ? 'diverge__fill--neg' : ''}`}
          style={negative ? { right: '50%', width: `${width}%` } : { left: '50%', width: `${width}%` }} />
      </span>
      <span className={`diverge__value ${negative ? 'warn' : ''}`}>{value.toFixed(2)}</span>
    </span>
  );
}
